/**
 * WebsiteShell — product-area layout with top header and sidebar nav.
 *
 * Wraps all product pages under /websites/:websiteId/*.
 * Renders a persistent header + left sidebar + <Outlet /> for page content.
 */

import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { logout } from "../api/auth.js";
import { useSession } from "../hooks/use-session.js";
import { useFirstOrg } from "../hooks/use-orgs.js";
import { useSubscription } from "../hooks/use-billing.js";
import { ImpersonationBanner } from "./impersonation-banner.js";
import { SubscriptionStatusBanner } from "./subscription-status-banner.js";

interface NavLinkConfig {
  to: string;
  label: string;
  end?: boolean;
}

const PRIMARY_NAV: NavLinkConfig[] = [
  { to: "", label: "Dashboard", end: true },
  { to: "scan", label: "Scan" },
  { to: "report", label: "Report" },
  { to: "statement", label: "Statement" },
  { to: "recurring", label: "Monitoring" },
  { to: "settings", label: "Settings" },
];

const MORE_NAV: NavLinkConfig[] = [
  { to: "documents", label: "Documents" },
  { to: "templates", label: "Templates" },
];

export function WebsiteShell() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const { user, impersonating } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const org = useFirstOrg();
  const { data: subscriptionData } = useSubscription(org?.id);
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const qc = useQueryClient();

  const isOnMorePage = MORE_NAV.some(({ to }) =>
    location.pathname.includes(`/websites/${websiteId}/${to}`),
  );
  const showMore = moreOpen || isOnMorePage;

  async function handleLogout() {
    await logout();
    qc.clear();
    navigate("/sign-in");
  }

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll when sidebar is open
  useEffect(() => {
    if (mobileSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileSidebarOpen]);

  // Focus trap and Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!mobileSidebarOpen) return;

      if (e.key === "Escape") {
        setMobileSidebarOpen(false);
        hamburgerRef.current?.focus();
        return;
      }

      if (e.key === "Tab" && sidebarRef.current) {
        const focusable = sidebarRef.current.querySelectorAll<HTMLElement>(
          'a, button, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [mobileSidebarOpen],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Focus close button when sidebar opens
  useEffect(() => {
    if (mobileSidebarOpen && sidebarRef.current) {
      const closeBtn = sidebarRef.current.querySelector<HTMLElement>(
        ".mobile-nav__close",
      );
      closeBtn?.focus();
    }
  }, [mobileSidebarOpen]);

  return (
    <>
      {impersonating && (
        <ImpersonationBanner impersonatingAdminId={impersonating} />
      )}
      <div className="ws-shell">
        <a href="#main-content" className="skip-to-content">
          Skip to main content
        </a>
        <header className="ws-shell__header">
          <div className="ws-shell__header-left">
            <button
              ref={hamburgerRef}
              type="button"
              className="hamburger-btn ws-hamburger-btn"
              onClick={() => setMobileSidebarOpen(true)}
              aria-expanded={mobileSidebarOpen}
              aria-controls="ws-mobile-sidebar"
              aria-label="Open sidebar navigation"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <Link to="/app" className="ws-shell__logo">
              Accessi<span>Monitor</span>
            </Link>
          </div>
          <nav className="ws-shell__header-nav ws-shell__header-nav--desktop" aria-label="User menu">
            <Link to="/websites" className="ws-shell__nav-link">
              My Websites
            </Link>
            <Link to="/billing" className="ws-shell__nav-link">
              Billing
            </Link>
            <span className="ws-shell__email">{user?.email ?? ""}</span>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => void handleLogout()}
            >
              Sign out
            </button>
          </nav>
        </header>

        <SubscriptionStatusBanner
          subscription={subscriptionData?.subscription ?? null}
        />

        {/* Mobile sidebar overlay */}
        {mobileSidebarOpen && (
          <div
            className="mobile-nav-backdrop"
            onClick={() => setMobileSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        <div
          ref={sidebarRef}
          id="ws-mobile-sidebar"
          className={`mobile-nav ws-mobile-nav ${mobileSidebarOpen ? "mobile-nav--open" : ""}`}
          role="dialog"
          aria-modal={mobileSidebarOpen || undefined}
          aria-label="Sidebar navigation"
          aria-hidden={!mobileSidebarOpen}
        >
          <div className="mobile-nav__header">
            <span className="mobile-nav__title">Navigation</span>
            <button
              type="button"
              className="mobile-nav__close"
              onClick={() => {
                setMobileSidebarOpen(false);
                hamburgerRef.current?.focus();
              }}
              aria-label="Close sidebar navigation"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <nav className="mobile-nav__links">
            {PRIMARY_NAV.map(({ to, label, end }) => (
              <NavLink
                key={label}
                to={`/websites/${websiteId}${to ? `/${to}` : ""}`}
                end={end}
                className={({ isActive }) =>
                  isActive ? "mobile-nav__link--active" : ""
                }
              >
                {label}
              </NavLink>
            ))}

            <div className="mobile-nav__divider" />

            {MORE_NAV.map(({ to, label, end }) => (
              <NavLink
                key={label}
                to={`/websites/${websiteId}${to ? `/${to}` : ""}`}
                end={end}
                className={({ isActive }) =>
                  isActive ? "mobile-nav__link--active" : ""
                }
              >
                {label}
              </NavLink>
            ))}

            <div className="mobile-nav__divider" />

            <Link to="/websites">My Websites</Link>
            <Link to="/billing">Billing</Link>
            <Link to="/help">Help</Link>
            <button
              type="button"
              className="mobile-nav__link-btn"
              onClick={() => {
                setMobileSidebarOpen(false);
                void handleLogout();
              }}
            >
              Sign out
            </button>
          </nav>
        </div>

        <div className="ws-shell__body">
          <nav className="ws-shell__sidebar ws-shell__sidebar--desktop" aria-label="Product navigation">
            {PRIMARY_NAV.map(({ to, label, end }) => (
              <NavLink
                key={label}
                to={`/websites/${websiteId}${to ? `/${to}` : ""}`}
                end={end}
                className={({ isActive }) =>
                  `ws-shell__nav-item${isActive ? " ws-shell__nav-item--active" : ""}`
                }
              >
                {label}
              </NavLink>
            ))}

            <div className="ws-shell__nav-divider" />

            <button
              type="button"
              className="ws-shell__nav-toggle"
              onClick={() => setMoreOpen(!moreOpen)}
              aria-expanded={showMore}
              aria-controls="more-nav"
            >
              <span className="ws-shell__nav-toggle-icon" aria-hidden="true">
                {showMore ? "\u25BE" : "\u25B8"}
              </span>
              More
            </button>

            {showMore && (
              <div id="more-nav" role="group" aria-label="More navigation">
                {MORE_NAV.map(({ to, label, end }) => (
                  <NavLink
                    key={label}
                    to={`/websites/${websiteId}${to ? `/${to}` : ""}`}
                    end={end}
                    className={({ isActive }) =>
                      `ws-shell__nav-item ws-shell__nav-item--advanced${isActive ? " ws-shell__nav-item--active" : ""}`
                    }
                  >
                    {label}
                  </NavLink>
                ))}
              </div>
            )}

            <div className="ws-shell__nav-divider" />
            <Link
              to="/help"
              className="ws-shell__nav-item"
            >
              Help
            </Link>
          </nav>

          <main id="main-content" className="ws-shell__main">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}
